import { GoabButton, GoabText } from '@abgov/angular-components';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ResponsiveService } from '../../services/responsive.service';

export type LoadMoreResultsState =
  | 'idle'
  | 'loading-initial'
  | 'auto'
  | 'error'
  | 'done';

/**
 * Presentational "load more" footer shared by every paginated results surface
 * (search results, questionnaire results; list view and map view).
 *
 * Owns only the button / summary-copy state machine. Skeleton cards and the
 * scroll-triggered auto-load sentinel live with the consumer's result grid.
 */
@Component({
  selector: 'common-load-more-results',
  imports: [GoabButton, GoabText],
  templateUrl: './load-more-results.component.html',
  styleUrl: './load-more-results.component.scss',
})
export class LoadMoreResultsComponent {
  private readonly responsive = inject(ResponsiveService);
  private readonly injector = inject(Injector);

  private readonly idleLabelContent =
    viewChild<ElementRef<HTMLElement>>('idleLabelContent');
  private readonly loadingLabelContent = viewChild<ElementRef<HTMLElement>>(
    'loadingLabelContent'
  );

  totalCount = input.required<number>();
  loadedCount = input.required<number>();
  batchSize = input<number>(12);
  state = input<LoadMoreResultsState>('idle');
  itemLabelSingular = input<string>('care home');
  itemLabelPlural = input<string>('care homes');

  loadMoreClicked = output<void>();
  retryClicked = output<void>();

  protected readonly remainingCount = computed(() =>
    Math.max(0, this.totalCount() - this.loadedCount())
  );

  protected readonly nextBatchCount = computed(() =>
    Math.min(this.batchSize(), this.remainingCount())
  );

  protected readonly hasMore = computed(() => this.remainingCount() > 0);

  protected readonly showButton = computed(
    () =>
      this.hasMore() &&
      (this.state() === 'idle' ||
        this.state() === 'loading-initial' ||
        this.state() === 'error')
  );

  protected readonly isLoadingInitial = computed(
    () => this.state() === 'loading-initial'
  );

  /**
   * Natural (unconstrained) pixel width of the wider of the idle/loading label variants.
   * CSS alone can't hug-size this button: the label content sits inside a CSS grid
   * overlap (so toggling labels never shifts the button's size), and a grid item's
   * `display` is blockified per spec, which otherwise inflates intrinsic-size
   * calculations up through the button. Measuring with detached clones sidesteps that
   * entirely and gives an exact, deterministic width.
   */
  protected readonly measuredContentWidthPx = signal<number | null>(null);

  // Figma spec: hug-width centered button on desktop, full list-container width on mobile.
  // The shadow-DOM button only reads the `--width` custom property (`width: var(--width,
  // auto)`) — plain `width` on an ancestor just resizes an invisible layout box the
  // (overflow:visible) shadow button ignores entirely.
  protected readonly buttonWidthVar = computed<string | undefined>(() => {
    if (!this.responsive.isLarge()) {
      return '100%'; // mobile: span the list-container width per design
    }
    const px = this.measuredContentWidthPx();
    return px !== null ? `${px}px` : undefined;
  });

  protected readonly buttonLabel = computed(() => {
    const n = this.nextBatchCount();
    const item = n === 1 ? this.itemLabelSingular() : this.itemLabelPlural();
    return `Load ${n} more ${item}`;
  });

  constructor() {
    // Re-measure whenever the rendered label text changes (e.g. remaining count changes).
    // The extra setTimeout lets the goab-button wrapper finish its own deferred
    // (isReady-flag-gated) shadow DOM render before we read its padding.
    effect(() => {
      this.buttonLabel();
      this.itemLabelPlural();
      afterNextRender(() => setTimeout(() => this.measureLabelWidths()), {
        injector: this.injector,
      });
    });
  }

  protected readonly summaryText = computed(() => {
    const total = this.totalCount();
    const loaded = this.loadedCount();

    if (this.state() === 'done') {
      const item =
        total === 1 ? this.itemLabelSingular() : this.itemLabelPlural();
      return `Showing all ${total} ${item}`;
    }

    if (!this.hasMore()) {
      return loaded === 1
        ? `Showing 1 ${this.itemLabelSingular()}`
        : `Showing ${loaded} ${this.itemLabelPlural()}`;
    }

    return `Showing ${loaded} of ${total}`;
  });

  protected handleButtonClick(): void {
    if (this.state() === 'error') {
      this.retryClicked.emit();
    } else {
      this.loadMoreClicked.emit();
    }
  }

  private measureLabelWidths(): void {
    const idle = this.idleLabelContent()?.nativeElement;
    const loading = this.loadingLabelContent()?.nativeElement;
    if (!idle || !loading) {
      return;
    }
    const contentWidth = Math.max(
      this.measureNaturalWidth(idle),
      this.measureNaturalWidth(loading)
    );
    const width = Math.ceil(
      contentWidth + this.measureButtonHorizontalPadding()
    );
    this.measuredContentWidthPx.set(width);
  }

  /**
   * The button's padding lives on the shadow-DOM `<button>`, inside `goa-button`'s shadow
   * root — not on the host elements this component can style directly. Reading it here
   * (rather than hardcoding a value) keeps the measured width correct if the design
   * system's padding ever changes.
   */
  private measureButtonHorizontalPadding(): number {
    const shadowButton = this.idleLabelContent()
      ?.nativeElement.closest('goab-button')
      ?.querySelector('goa-button')
      ?.shadowRoot?.querySelector('button');
    if (!shadowButton) {
      return 0;
    }
    const style = getComputedStyle(shadowButton);
    return parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  }

  private measureNaturalWidth(el: HTMLElement): number {
    const clone = el.cloneNode(true) as HTMLElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.width = 'auto';
    clone.style.whiteSpace = 'nowrap';
    document.body.appendChild(clone);
    const width = clone.getBoundingClientRect().width;
    document.body.removeChild(clone);
    return width;
  }
}
